import { useCallback, useMemo, useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Progress,
  Select,
  TableContainer,
} from "@chakra-ui/react";
import { IncrementalPagination } from "../../../../components/Pagination/IncrementalPagination";
import { StyledTable } from "../../../../components/StyledTable/StyledTable";
import { HeaderCell } from "./HeaderCell";
import { useGetMyAdminCompletedStudies } from "../../../../api/StudyApi";
import { AdminCompletedStudy } from "../../../../types/AdminCompletedStudy";
import AdminCompletedStudyRow from "./AdminCompletedStudyRow";
import { PaymentStatus } from "../../../../types/PaymentStatus";
import { THEME_COLORS } from "../../../../base/theme/foundations/colors";
import DateRangePicker from "../../../../components/DateRangePicker/DateRangePicker";
import GroupProviderSelect from "../../../../components/MultiSelect/GroupProviderSelect";
import { useStreamAdminCompletedStudiesCSV } from "../../../../hooks/useStreamAdminCompletedStudiesCSV";

interface Props {
  groupId: string;
  groupName: string;
}

const AdminCompletedStudiesTable: React.FC<Props> = ({ groupId, groupName }) => {
  const [page, setPage] = useState(1);
  const [perPage] = useState(25);
  const [radiologistId, setRadiologistId] = useState<string | undefined>();
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<PaymentStatus | null>(
    null,
  );
  const [paymentStatusReasonFilter, setPaymentStatusReasonFilter] = useState<string | null>(
    null,
  );
  const [payableToReaderFilter, setPayableToReaderFilter] = useState<
    "true" | "false" | null
  >(null);
  const [associatedToUserFilter, setAssociatedToUserFilter] = useState<"true" | "false">(
    "true",
  );
  const [datesFilter, setDatesFilter] = useState<Date[]>([]);

  // Add useEffect to reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [
    radiologistId,
    paymentStatusFilter,
    paymentStatusReasonFilter,
    payableToReaderFilter,
    associatedToUserFilter,
    datesFilter,
  ]);

  const { data, isLoading } = useGetMyAdminCompletedStudies(
    page,
    perPage,
    groupId,
    paymentStatusFilter,
    paymentStatusReasonFilter,
    payableToReaderFilter,
    associatedToUserFilter,
    datesFilter,
    radiologistId,
  );
  const { streamReport, isStreaming, progress } = useStreamAdminCompletedStudiesCSV({
    groupId,
    groupName,
    paymentStatus: paymentStatusFilter,
    paymentStatusReason: paymentStatusReasonFilter,
    payableToReader: payableToReaderFilter,
    associatedToUser: associatedToUserFilter,
    dateRange: datesFilter,
    radiologistId,
  });
  const payableToReaderFilterOptions = useMemo(() => {
    if (!paymentStatusFilter) {
      return ["true", "false"];
    } else if (paymentStatusFilter === "NONPAYABLE") {
      if (payableToReaderFilter === "true") {
        setPayableToReaderFilter("false");
      }
      return ["false"];
    } else {
      if (payableToReaderFilter === "false") {
        setPayableToReaderFilter("true");
      }
      return ["true"];
    }
  }, [payableToReaderFilter, paymentStatusFilter]);

  const paymentStatusFilterOptions = useMemo(() => {
    if (!payableToReaderFilter) {
      return ["PENDING", "PAYABLE", "PAID", "NONPAYABLE"];
    } else if (payableToReaderFilter === "true") {
      if (paymentStatusFilter === "NONPAYABLE") {
        setPaymentStatusFilter(null);
      }
      return ["PENDING", "PAYABLE", "PAID"];
    } else {
      if (paymentStatusFilter !== "NONPAYABLE") {
        setPaymentStatusFilter(null);
      }
      return ["NONPAYABLE"];
    }
  }, [payableToReaderFilter, paymentStatusFilter]);

  const onDatesChange = useCallback((dates: Date[]) => {
    if (dates.length === 2) {
      setDatesFilter(dates);
    } else {
      setDatesFilter([]);
    }
  }, []);

  const tableColumns: HeaderCell[] = [
    { key: "orderCode", label: "Facility Code" },
    { key: "orderDescription", label: "Master Code" },
    { key: "readingProvider", label: "Reading Provider" },
    { key: "radiologistId", label: "Provider ID" },
    { key: "rvu", label: "RVU" },
    { key: "activeAlerts", label: "Active Alerts" },
    { key: "payable", label: "Payable" },
    { key: "prevailingUsdPerRvu", label: "Rate/RVU" },
    { key: "prevailingDollarsPayable", label: "Prevailing Dollars Payable" },
    { key: "dateFinalized", label: "Date Finalized" },
    { key: "paymentStatus", label: "Payment Status" },
    { key: "paymentStatusReason", label: "Status Reason" },
    { key: "originalFacility", label: "Facility" },
    // { key: "isRadiologistOnShift", label: "On Shift" },
    // { key: "inCreditShiftWindow", label: "In Credit Window" },
    // { key: "shiftName", label: "Shift Name" },
    // { key: "rvuTarget", label: "RVU Target" },
    // { key: "shiftWorkDate", label: "Shift Work Date" },
  ];

  const studyRow = (study: AdminCompletedStudy) => (
    <AdminCompletedStudyRow study={study} groupName={groupName} />
  );

  return (
    <Box>
      <Grid templateColumns="repeat(3, 1fr)" my="4" gap={4}>
        <FormControl variant="floating" maxW="full" alignSelf="center">
          <Select
            minH="56px"
            textColor="white"
            placeholder="Select Payable to Reader"
            value={payableToReaderFilter ?? ""}
            onChange={(e) => setPayableToReaderFilter(e.target.value as "true" | "false")}
            sx={{
              option: {
                backgroundColor: "gray.700",
                color: "white",
              },
            }}
          >
            {payableToReaderFilterOptions.map((v) => (
              <option key={v} value={v}>
                {v === "true" ? "Yes" : "No"}
              </option>
            ))}
          </Select>
          <FormLabel style={{ backgroundColor: THEME_COLORS.darkBlue[900] }}>
            Payable to Reader
          </FormLabel>
        </FormControl>
        <FormControl variant="floating" maxW="full">
          <Select
            textColor="white"
            placeholder="Select Payment Status"
            minH="56px"
            value={paymentStatusFilter ?? ""}
            sx={{
              option: {
                backgroundColor: "gray.700",
                color: "white",
              },
            }}
            onChange={(e) => {
              setPaymentStatusFilter(e.target.value as PaymentStatus);
              if ((e.target.value as PaymentStatus) !== "NONPAYABLE") {
                setPaymentStatusReasonFilter(null);
              }
            }}
          >
            {paymentStatusFilterOptions.map((v) => (
              <option value={v} key={v}>
                {v}
              </option>
            ))}
          </Select>
          <FormLabel style={{ backgroundColor: THEME_COLORS.darkBlue[900] }}>
            Payment Status
          </FormLabel>
        </FormControl>
        {paymentStatusFilter === "NONPAYABLE" && (
          <FormControl variant="floating" maxW="full">
            <Select
              textColor="white"
              placeholder="Select Non-Payable Reason"
              minH="56px"
              sx={{
                option: {
                  backgroundColor: "gray.700",
                  color: "white",
                },
              }}
              value={paymentStatusReasonFilter ?? ""}
              onChange={(e) => setPaymentStatusReasonFilter(e.target.value)}
            >
              {[
                "NOT_OPEN_DURING_CY",
                "CPT_CODE_NOT_FOUND",
                "READING_PROVIDER_NOT_TARGETED",
                "TARGETING_DISABLED",
                "EXCLUDED_PROCEDURE",
                "IR_MG_STUDY",
              ].map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </Select>
            <FormLabel style={{ backgroundColor: THEME_COLORS.darkBlue[900] }}>
              Non-Payable Reason
            </FormLabel>
          </FormControl>
        )}
        <DateRangePicker onDatesChange={onDatesChange} />
        <GridItem colSpan={2}>
          <GroupProviderSelect
            groupId={groupId}
            onChange={(newValue) => {
              if (newValue) {
                setRadiologistId((newValue as { label: string; value: string }).value);
              } else {
                setRadiologistId(undefined);
              }
            }}
          />
        </GridItem>
        <FormControl variant="floating" maxW="full" alignSelf="center">
          <Select
            minH="56px"
            textColor="white"
            placeholder="Select if associated with user"
            sx={{
              option: {
                backgroundColor: "gray.700",
                color: "white",
              },
            }}
            value={associatedToUserFilter ?? "true"}
            onChange={(e) => setAssociatedToUserFilter(e.target.value as "true" | "false")}
          >
            {[
              { label: "Yes", value: "true" },
              { label: "No", value: "false" },
            ].map(({ label, value }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
          <FormLabel style={{ backgroundColor: THEME_COLORS.darkBlue[900] }}>
            Associated with User
          </FormLabel>
        </FormControl>
        <Box>
          <Button
            variant="outline"
            colorScheme="brandYellow"
            w="full"
            isDisabled={isStreaming}
            onClick={() => {
              streamReport();
            }}
            mb={isStreaming ? "0" : "4"}
          >
            {isStreaming ? "Generating..." : "Export Report"}
          </Button>
          {isStreaming && (
            <Progress
              value={progress}
              mt="2"
              size="sm"
              colorScheme="brandYellow"
              borderRadius="md"
              w="full"
            />
          )}
        </Box>
      </Grid>

      <TableContainer>
        <StyledTable
          columns={tableColumns}
          data={data?.docs ?? []}
          customRowRenderers={studyRow}
          loading={isLoading}
          rowHeight={40}
        />
      </TableContainer>
      <IncrementalPagination
        currentPage={page}
        setPage={setPage}
        hasMore={isLoading ? false : (data?.docs.length ?? 0) === (data?.perPage ?? 0)}
      />
    </Box>
  );
};

export default AdminCompletedStudiesTable;
